import {
  Alert,
  Badge,
  Box,
  Button,
  Divider,
  Group,
  List,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { LoaderFunctionArgs, MetaFunction, json } from "@remix-run/node";
import { Link, useLoaderData, useNavigation } from "@remix-run/react";
import { IconInfoCircle } from "@tabler/icons-react";

import { BusinessReviewBadge } from "~/components/business-review-badge";
import { DoctorCard } from "~/components/doctor-card";
import {
  DEFAULT_OG_IMAGE_EN,
  DEFAULT_OG_IMAGE_FR,
  PAGE_TITLE,
} from "~/constants";
import { countryToCurrency } from "~/constants/currencies";
import {
  getSpecialtyLocationPage,
  specialtyLocationPages,
} from "~/data/specialty-location-pages";
import i18n from "~/localization/i18n";
import i18nextServer from "~/localization/i18n.server";
import { useTranslation } from "~/hooks/use-typed-translation";
import { getDoctorProfiles } from "~/services/get-doctor-profiles";
import utilityClasses from "~/styles/utilities.module.scss";
import { DoctorProfile } from "~/types";
import { getCentralizedMeta } from "~/utils/meta-helper";
import { normalizeString } from "~/utils/normalize-string";

const normalizeForMatch = (value?: string | null) =>
  normalizeString(value ?? "").toLocaleLowerCase();

const getLocalized = <T,>(value: { fr: T; en: T }, locale: string) =>
  locale === "en" ? value.en : value.fr;

function SpecialtyCardLink({
  to,
  label,
  cityLabel,
}: {
  to: string;
  label: string;
  cityLabel: string;
}) {
  const navigation = useNavigation();
  const isLoading = navigation.state !== "idle" && navigation.location?.pathname === to;

  return (
    <Paper
      component={Link}
      to={to}
      withBorder
      p="sm"
      radius="sm">
      <Group justify="space-between" align="center" wrap="nowrap">
        <div>
          <Text fw={600} fz={14}>
            {label}
          </Text>
          <Text c="dimmed" fz={13}>
            {cityLabel}
          </Text>
        </div>
        {isLoading ? <Loader size="xs" /> : null}
      </Group>
    </Paper>
  );
}

const doctorMatchesSpecialty = (
  doctor: DoctorProfile,
  aliases: string[]
) => {
  const haystack = [
    doctor.specialization,
    ...(doctor.specialties ?? []),
    ...(doctor.services?.map(service => service.label) ?? []),
  ]
    .map(normalizeForMatch)
    .join(" ");

  return aliases.some(alias => haystack.includes(normalizeForMatch(alias)));
};

const doctorIsLicensedInCountry = (
  doctor: DoctorProfile,
  countryCode: string
) => {
  if (!doctor.licensedCountries?.length) return true;

  return doctor.licensedCountries.some(
    country => country.toLocaleUpperCase() === countryCode
  );
};

const getDoctorAreaServed = (doctor: DoctorProfile) => {
  if (doctor.consultationModes?.includes("teleconsultation")) {
    return "Worldwide";
  }

  return doctor.licensedCountries?.length
    ? doctor.licensedCountries.map(country => ({
        "@type": "Country",
        identifier: country.toLocaleUpperCase(),
      }))
    : "Worldwide";
};

const getDoctorAggregateRating = (doctor: DoctorProfile) => {
  const average = doctor.ratingSummary?.average;
  const count = doctor.ratingSummary?.count ?? 0;

  return typeof average === "number" && count >= 3
    ? {
        "@type": "AggregateRating",
        ratingValue: average,
        ratingCount: count,
        bestRating: 5,
        worstRating: 1,
      }
    : undefined;
};

const getStructuredData = ({
  doctors,
  locale,
  origin,
  pathname,
  specialtyLabel,
  cityLabel,
  faqs,
}: {
  doctors: DoctorProfile[];
  locale: string;
  origin: string;
  pathname: string;
  specialtyLabel: string;
  cityLabel: string;
  faqs: Array<{ question: string; answer: string }>;
}) => {
  const pageUrl = `${origin}${pathname}`;

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "MedicalWebPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: `${specialtyLabel} ${cityLabel}`,
        inLanguage: locale,
      },
      {
        "@type": "ItemList",
        "@id": `${pageUrl}#doctors`,
        name: `${specialtyLabel} ${cityLabel}`,
        itemListElement: doctors.map((doctor, index) => ({
          "@type": "ListItem",
          position: index + 1,
          item: {
            "@type": "Physician",
            name: doctor.name,
            medicalSpecialty: doctor.specialization,
            url: `${origin}/${locale}/doctor/${doctor.slug}`,
            image: doctor.image,
            address: doctor.location
              ? {
                  "@type": "PostalAddress",
                  addressLocality: doctor.location,
                }
              : undefined,
            areaServed: getDoctorAreaServed(doctor),
            knowsLanguage: doctor.languages,
            hasCredential: doctor.licenseNumber
              ? {
                  "@type": "EducationalOccupationalCredential",
                  credentialCategory: "Medical licence",
                  identifier: doctor.licenseNumber,
                }
              : undefined,
            aggregateRating: getDoctorAggregateRating(doctor),
          },
        })),
      },
      {
        "@type": "FAQPage",
        "@id": `${pageUrl}#faq`,
        mainEntity: faqs.map(faq => ({
          "@type": "Question",
          name: faq.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.answer,
          },
        })),
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumb`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: PAGE_TITLE,
            item: `${origin}/${locale}`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: locale === "fr" ? "Spécialités" : "Specialties",
            item: `${origin}/${locale}/specialites`,
          },
          {
            "@type": "ListItem",
            position: 3,
            name: `${specialtyLabel} ${cityLabel}`,
            item: pageUrl,
          },
        ],
      },
    ],
  };
};

export const meta: MetaFunction<typeof loader> = ({ data, location }) => {
  if (!data) return [];

  return getCentralizedMeta({
    title: data.title,
    description: data.description,
    image: data.locale === "fr" ? DEFAULT_OG_IMAGE_FR : DEFAULT_OG_IMAGE_EN,
    url: data.origin + location.pathname,
    locale: data.locale,
    keywords: data.keywords,
    alternateLanguages: {
      en: `${data.origin}/en/specialites/${data.specialtySlug}/${data.citySlug}`,
      fr: `${data.origin}/fr/specialites/${data.specialtySlug}/${data.citySlug}`,
    },
  });
};

export async function loader({ request, params }: LoaderFunctionArgs) {
  const lang = params.lang ?? "";
  const locale = i18n.supportedLngs.includes(lang.toLocaleLowerCase())
    ? lang
    : await i18nextServer.getLocale(request);
  const specialtySlug = params.specialty ?? "";
  const citySlug = params.city ?? "";
  const page = getSpecialtyLocationPage(specialtySlug, citySlug);

  if (!page) {
    throw new Response("Specialty page not found", { status: 404 });
  }

  const origin = new URL(request.url).origin;
  const pathname = new URL(request.url).pathname;
  const doctorsRes = await getDoctorProfiles({ pageSize: 100 });
  const allDoctors = doctorsRes.data ?? [];
  const doctors = allDoctors.filter(
    doctor =>
      doctorMatchesSpecialty(doctor, page.aliases) &&
      doctorIsLicensedInCountry(doctor, page.countryCode)
  );
  const specialtyLabel = getLocalized(page.specialtyLabel, locale);
  const countryLabel = getLocalized(page.countryLabel, locale);
  const currency =
    countryToCurrency[
      page.countryCode as keyof typeof countryToCurrency
    ] ?? "XOF";
  const title = getLocalized(page.title, locale);
  const description = getLocalized(page.description, locale);
  const faqs = page.faqs.map(faq => ({
    question: getLocalized(faq.question, locale),
    answer: getLocalized(faq.answer, locale),
  }));

  return json({
    locale,
    origin,
    pathname,
    specialtySlug,
    citySlug,
    countryCode: page.countryCode,
    title,
    description,
    keywords: `${specialtyLabel}, ${page.cityLabel}, ${countryLabel}, médecin, docteur, rendez-vous médical, téléconsultation`,
    specialtyLabel,
    cityLabel: page.cityLabel,
    countryLabel,
    currency,
    intro: getLocalized(page.intro, locale),
    benefits: getLocalized(page.benefits, locale),
    faqs,
    searchHref: `/${locale}?q=${specialtySlug}&country=${page.countryCode}`,
    doctors,
    relatedPages: specialtyLocationPages.filter(
      related =>
        related.citySlug === citySlug &&
        related.specialtySlug !== specialtySlug
    ),
    structuredData: getStructuredData({
      doctors,
      locale,
      origin,
      pathname,
      specialtyLabel,
      cityLabel: page.cityLabel,
      faqs,
    }),
  });
}

export default function SpecialtyLocationRoute() {
  const { t } = useTranslation();
  const {
    locale,
    specialtyLabel,
    cityLabel,
    countryLabel,
    currency,
    intro,
    benefits,
    faqs,
    searchHref,
    doctors,
    relatedPages,
    structuredData,
  } = useLoaderData<typeof loader>();

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />

      <main className={utilityClasses.container}>
        <Box py={{ base: "2.5rem", sm: "4rem" }} ta="center">
          <Group justify="center" gap="xs" mb="sm">
            <Badge variant="light" color="primary">
              {cityLabel}
            </Badge>
            <Badge variant="light" color="gray">
              {countryLabel}
            </Badge>
            <Badge variant="outline">
              {t("specialty-page-specialty")}
            </Badge>
          </Group>
          <Title order={1} fw={700} fz={{ base: 28, sm: 38 }} lh={1.15}>
            {specialtyLabel} {locale === "fr" ? "à" : "in"} {cityLabel}
          </Title>
          <Text mt="md" maw={720} mx="auto" fz={{ base: 15, sm: 17 }} c="dimmed" lh={1.6}>
            {intro}
          </Text>
          <BusinessReviewBadge mt="md" />
          <Button component={Link} to={searchHref} mt="xl" variant="light">
            {t("specialty-page-view-filtered-search")}
          </Button>
        </Box>

        <Divider />

        <Box py={{ base: "2rem", sm: "3.25rem" }}>
          <Group justify="space-between" align="end" mb="lg">
            <div>
              <Title order={2} fz={{ base: 22, sm: 28 }} fw={650}>
                {t("specialty-page-available-practitioners")}
              </Title>
              <Text mt={4} c="dimmed" fz={14}>
                {doctors.length
                  ? t("specialty-page-matching-profiles", { count: doctors.length })
                  : t("specialty-page-no-matching-profiles")}
              </Text>
            </div>
          </Group>

          {doctors.length ? (
            <Group align="stretch" gap="md">
              {doctors.map(doctor => (
                <DoctorCard
                  key={doctor.id}
                  doctor={doctor}
                  currency={currency}
                />
              ))}
            </Group>
          ) : (
            <Alert icon={<IconInfoCircle size={18} />} color="blue" variant="light">
              {t("specialty-page-empty-doctors")}
            </Alert>
          )}
        </Box>

        <Divider />

        <Box py={{ base: "2rem", sm: "3.25rem" }}>
          <Title order={2} fz={{ base: 22, sm: 28 }} fw={650}>
            {t("specialty-page-why-laruche")}
          </Title>
          <List mt="md" spacing="sm" c="dimmed" fz={15}>
            {benefits.map(benefit => (
              <List.Item key={benefit}>{benefit}</List.Item>
            ))}
          </List>
        </Box>

        <Divider />

        <Box py={{ base: "2rem", sm: "3.25rem" }}>
          <Title order={2} fz={{ base: 20, sm: 24 }} fw={650}>
            {t("specialty-page-other-specialties", { city: cityLabel })}
          </Title>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} mt="md" spacing="sm">
            {relatedPages.map(page => (
              <SpecialtyCardLink
                key={page.specialtySlug}
                to={`/${locale}/specialites/${page.specialtySlug}/${page.citySlug}`}
                label={getLocalized(page.specialtyLabel, locale)}
                cityLabel={cityLabel}
              />
            ))}
          </SimpleGrid>
        </Box>

        <Divider />

        <Box py={{ base: "2rem", sm: "3.25rem" }}>
          <Title order={2} fz={{ base: 22, sm: 28 }} fw={650}>
            {t("specialty-page-faq")}
          </Title>
          <Stack mt="lg" gap="lg">
            {faqs.map(faq => (
              <section key={faq.question}>
                <Title order={3} fz={17} fw={600}>
                  {faq.question}
                </Title>
                <Text mt={6} c="dimmed" fz={15} lh={1.6}>
                  {faq.answer}
                </Text>
              </section>
            ))}
          </Stack>
        </Box>
      </main>
    </>
  );
}
